import React, { useEffect, useState } from 'react';
import { get, ref } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';

// Earnest money tasks: one tap to text the TC or the agent. Text opens an editable message, and Send marks the task done.
const fmt = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function EmdQuickText({ task, ownerUid, clientName, dealAddress, onSent }) {
  const { user } = useAuth();
  const taskName = task.name || task.title || '';
  const [people, setPeople] = useState({ tc: null, agent: null });
  const [open, setOpen] = useState(null); // null | 'tc' | 'agent'
  const [text, setText] = useState('');

  const isEmd = /\bEMD\b|earnest money/i.test(taskName) && !/^text client/i.test(taskName) && task.mode !== 'auto' && !task.automatic;

  useEffect(() => {
    if (!isEmd) return;
    get(ref(database, 'users')).then((snap) => {
      if (!snap.exists()) return;
      const all = snap.val();
      // Phone and first name come from each person's My Profile (users/{uid}/profile).
      const phoneOf = (u) => u?.profile?.phone || u?.phone || '';
      const nameOf = (u) => u?.profile?.firstName || (u?.displayName || '').split(' ')[0] || 'there';
      const tcEntry = Object.values(all).find((u) => u.role === 'TC' && phoneOf(u));
      const agentEntry = ownerUid ? all[ownerUid] : null;
      setPeople({
        tc: tcEntry ? { name: nameOf(tcEntry), phone: phoneOf(tcEntry) } : null,
        agent: agentEntry ? { name: nameOf(agentEntry), phone: phoneOf(agentEntry) } : null,
      });
    }).catch((err) => console.error('Could not load contacts for EMD texts', err));
  }, [isEmd, ownerUid]);

  if (!isEmd) return null;

  const senderName = user?.displayName || 'Kelly Olin';
  const due = fmt(task.dueDate);
  const messageFor = (who, first) => {
    if (who === 'tc') {
      return `Hi ${first}, ${clientName || 'my buyer'}'s earnest money for ${dealAddress || 'the property'} is due ${due || 'soon'}. Can you confirm it's received once it's in? - ${senderName}`;
    }
    return `Hi ${first}, reminder that ${clientName || 'your buyer'}'s earnest money for ${dealAddress || 'the property'} is due ${due || 'soon'}. Please confirm it's sent or scheduled. - ${senderName}`;
  };

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
  const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', pointerEvents: 'none', opacity: 0.5 };
  const btnCancel = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };

  const toggle = (who) => {
    if (open === who) { setOpen(null); return; }
    const person = people[who];
    setText(messageFor(who, (person?.name || '').split(' ')[0] || 'there'));
    setOpen(who);
  };

  const row = (who, label) => {
    const person = people[who];
    const phone = (person?.phone || '').replace(/[^0-9+]/g, '');
    return (
      <div key={who} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: '#1A1A1A', fontWeight: 600, minWidth: 110 }}>{label}</span>
        <button type="button" onClick={() => toggle(who)} disabled={!phone} style={phone ? btn : btnOff}>Text {label}</button>
        {!phone && <span style={{ fontSize: 12, color: '#5B6270' }}>No phone saved yet</span>}
        {open === who && phone && (
          <div style={{ width: '100%', marginTop: 6, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <a href={`sms:${phone}?body=${encodeURIComponent(text)}`} style={btn} onClick={() => onSent && onSent()}>Send</a>
              <button type="button" onClick={() => setOpen(null)} style={btnCancel}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ margin: '6px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {row('tc', 'TC')}
      {row('agent', 'Agent')}
    </div>
  );
}
